import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import { articles, type ArticleData, type ArticleSection } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article: ArticleData | undefined = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center py-20">
          <div className="text-center px-4">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-accent-100 mb-5">
              <i className="ri-file-search-line text-accent-500 text-2xl"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">Article Not Found</h1>
            <p className="text-sm text-foreground-500 mb-6">The article you are looking for does not exist yet.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const renderSection = (section: ArticleSection, idx: number) => {
    switch (section.type) {
      case 'content':
        return (
          <section key={section.id} id={section.id} className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">{section.title}</h2>
            {section.content?.map((p, i) => (
              <p key={i} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">{p}</p>
            ))}
          </section>
        );

      case 'benefits':
        return (
          <section key={section.id} id={section.id} className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">{section.title}</h2>
            {section.content?.map((p, i) => (
              <p key={i} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">{p}</p>
            ))}
            {section.items && (
              <div className="my-6 p-5 rounded-xl bg-accent-100/50 border border-accent-200/60">
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-3">Key Benefits of Using an Organized Spreadsheet:</h3>
                <ul className="flex flex-col gap-2 text-sm text-foreground-600">
                  {section.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <i className={`${item.icon || 'ri-checkbox-circle-fill'} text-accent-500 mt-0.5 flex-shrink-0`}></i>
                      <span><strong className="text-foreground-800">{item.title}:</strong> {item.desc}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        );

      case 'comparison':
        return (
          <section key={section.id} id={section.id} className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">{section.title}</h2>
            {section.content?.map((p, i) => (
              <p key={i} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">{p}</p>
            ))}
            {section.items && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {section.items.filter((item) => item.icon === 'ri-checkbox-circle-fill').map((item, i) => (
                  <div key={`pro-${i}`} className="p-4 rounded-xl bg-accent-50 border border-accent-200/50">
                    <div className="flex items-start gap-2.5">
                      <i className="ri-checkbox-circle-fill text-accent-500 mt-0.5 flex-shrink-0"></i>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-800 mb-0.5">{item.title}</h4>
                        <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {section.items && section.items.filter((item) => item.icon === 'ri-close-circle-line').length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {section.items.filter((item) => item.icon === 'ri-close-circle-line').map((item, i) => (
                  <div key={`con-${i}`} className="p-4 rounded-xl bg-background-100 border border-background-200/60">
                    <div className="flex items-start gap-2.5">
                      <i className="ri-close-circle-line text-foreground-400 mt-0.5 flex-shrink-0"></i>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-800 mb-0.5">{item.title}</h4>
                        <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        );

      case 'categories':
        return (
          <section key={section.id} id={section.id} className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">{section.title}</h2>
            {section.content?.map((p, i) => (
              <p key={i} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">{p}</p>
            ))}
            {section.items && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {section.items.map((item, i) => (
                  <div key={i} className="p-4 rounded-xl bg-background-100 border border-background-200/60">
                    <h4 className="text-sm font-semibold text-foreground-800 mb-1">{item.title}</h4>
                    <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            )}
            {article.slug === 'sugargoo-spreadsheet-ultimate-guide' && (
              <div className="text-center">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  Browse All Categories
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            )}
          </section>
        );

      case 'steps':
        return (
          <section key={section.id} id={section.id} className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">{section.title}</h2>
            {section.content?.map((p, i) => (
              <p key={i} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">{p}</p>
            ))}
            {section.steps && (
              <div className="space-y-5">
                {section.steps.map((s) => (
                  <div key={s.step} className="flex gap-4 p-5 rounded-xl bg-background-100 border border-background-200/60">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 flex-shrink-0">
                      <span className="text-sm font-bold text-primary-600">{s.step}</span>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-800 mb-1">{s.title}</h4>
                      <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        );

      case 'table':
        return (
          <section key={section.id} id={section.id} className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">{section.title}</h2>
            {section.content?.map((p, i) => (
              <p key={i} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">{p}</p>
            ))}
            {section.tableHeaders && section.tableRows && (
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm border border-background-200/60 rounded-xl overflow-hidden">
                  <thead>
                    <tr className="bg-background-100">
                      {section.tableHeaders.map((h, i) => (
                        <th key={i} className="text-left px-4 py-3 font-semibold text-foreground-800 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {section.tableRows.map((row, i) => (
                      <tr key={i} className={`border-t border-background-200/40 ${i % 2 === 0 ? 'bg-background-50' : 'bg-background-100/30'}`}>
                        {row.map((cell, j) => (
                          <td key={j} className={`px-4 py-3 whitespace-nowrap ${j === 1 ? 'text-accent-700 font-medium' : j === row.length - 1 ? 'text-foreground-500' : 'font-medium text-foreground-800'}`}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {section.content && section.content.length > 1 && (
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{section.content[section.content.length - 1]}</p>
            )}
          </section>
        );

      case 'faq':
        return (
          <section key={section.id} id={section.id} className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">{section.title}</h2>
            <div className="space-y-3">
              {section.faqs?.map((faq, i) => (
                <details key={i} className="group rounded-xl bg-background-100 border border-background-200/60 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <h3 className="text-sm font-semibold text-foreground-800 pr-4">{faq.q}</h3>
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-background-200 group-open:bg-primary-100 flex-shrink-0 transition-colors">
                      <i className="ri-add-line text-xs text-foreground-500 group-open:hidden"></i>
                      <i className="ri-subtract-line text-xs text-primary-600 hidden group-open:block"></i>
                    </div>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        <article>
          <header className="relative w-full min-h-[350px] md:min-h-[420px] flex items-end overflow-hidden bg-background-200">
            <img
              src={article.heroImage}
              alt={article.heroImageAlt}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-black/15"></div>
            <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 pb-10 md:pb-14">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="px-3 py-1 rounded-full bg-accent-400/80 text-background-50 text-xs font-medium">{article.category}</span>
                <span className="px-3 py-1 rounded-full bg-background-50/20 text-background-50/80 text-xs font-medium">Updated August 2026</span>
                <span className="px-3 py-1 rounded-full bg-background-50/20 text-background-50/80 text-xs font-medium">{article.readTime}</span>
              </div>
              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4">
                {article.metaTitle}
              </h1>
              <p className="text-sm md:text-base text-background-50/75 max-w-2xl">
                {article.metaDescription}
              </p>
            </div>
          </header>

          <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-8 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line text-[10px]"></i>
              <span className="text-foreground-600 font-medium">{article.title}</span>
            </nav>

            <section className="mb-10">
              {article.intro.map((p, i) => (
                <p key={i} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">{p}</p>
              ))}
            </section>

            <section className="mb-10 p-5 md:p-6 rounded-2xl bg-background-100 border border-background-200/60">
              <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-4">Table of Contents</h2>
              <ol className="flex flex-col gap-2 text-sm">
                {article.sections.map((s, i) => (
                  <li key={s.id} className="flex items-start gap-2">
                    <span className="font-semibold text-primary-500 flex-shrink-0">{i + 1}.</span>
                    <a href={`#${s.id}`} className="text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">{s.title.replace(/^\d+\.\s*/, '')}</a>
                  </li>
                ))}
              </ol>
            </section>

            {article.sections.map((s, i) => renderSection(s, i))}

            <section className="mb-10 p-6 md:p-8 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 text-center">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-3">
                {article.ctaTitle}
              </h2>
              <p className="text-sm text-background-50/85 max-w-md mx-auto mb-6">
                {article.ctaDesc}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-background-50 text-primary-600 font-semibold text-sm hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap"
                >
                  Explore Sugargoo Spreadsheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-700/40 text-background-50 font-semibold text-sm hover:bg-primary-700/60 transition-all cursor-pointer whitespace-nowrap border border-background-50/20"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back to Homepage
                </Link>
              </div>
            </section>

            <section className="p-5 rounded-xl bg-background-100 border border-background-200/60">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 flex-shrink-0">
                  <i className="ri-information-line text-secondary-600 text-sm"></i>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-foreground-700 mb-1">Important Disclaimer</h4>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    Sugargoo Sheet is an independent resource website. We are not affiliated with, endorsed by, or officially connected to Sugargoo in any way. All product information is sourced from publicly available community resources and should be independently verified before making any purchase decisions. Product availability, pricing, and quality may vary.
                  </p>
                </div>
              </div>
            </section>
          </div>
        </article>

        {article && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Article",
                "headline": article.metaTitle,
                "description": article.metaDescription,
                "image": article.heroImage,
                "datePublished": "2026-08-09",
                "dateModified": "2026-08-09",
                "author": {
                  "@type": "Organization",
                  "name": "Sugargoo Sheet",
                  "url": "https://sugargoosheet.art/"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "Sugargoo Sheet",
                  "logo": {
                    "@type": "ImageObject",
                    "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/1fc56da0b352904925ac780066826afe.jpeg"
                  }
                },
                "mainEntityOfPage": {
                  "@type": "WebPage",
                  "@id": `https://sugargoosheet.art/articles/${article.slug}`
                },
                "about": {
                  "@type": "Thing",
                  "name": "Sugargoo Spreadsheet"
                }
              })
            }}
          />
        )}

        {article && article.sections.some((s) => s.type === 'faq') && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": article.sections
                  .filter((s) => s.type === 'faq')
                  .flatMap((s) =>
                    (s.faqs || []).map((faq) => ({
                      "@type": "Question",
                      "name": faq.q,
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": faq.a
                      }
                    }))
                  )
              })
            }}
          />
        )}

        {article && article.sections.some((s) => s.type === 'table' || s.type === 'steps') && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "HowTo",
                "name": article.title,
                "description": article.metaDescription,
                "step": article.sections
                  .filter((s) => s.type === 'steps')
                  .flatMap((s) =>
                    (s.steps || []).map((step) => ({
                      "@type": "HowToStep",
                      "name": step.title,
                      "text": step.desc
                    }))
                  ),
                "supply": article.sections
                  .filter((s) => s.type === 'table')
                  .flatMap((s) =>
                    (s.tableHeaders || []).map((header) => ({
                      "@type": "HowToSupply",
                      "name": header
                    }))
                  )
              })
            }}
          />
        )}
      </main>
      <Footer />
    </div>
  );
}